import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, API } from "@/lib/api";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePaged, PageSize, Pager, ModuleTabs } from "@/components/Toolbar";
import { MonthPicker } from "@/components/MonthPicker";
import { fmtNumber, fmtCurrency } from "@/lib/format";
import { Plus, Trash2, Pencil, ChevronRight, Printer, FileSpreadsheet, FileUp, Banknote, ScanLine, Truck, Gauge, MoreVertical } from "lucide-react";
import { Combobox } from "@/components/Combobox";
import { Switch } from "@/components/ui/switch";
import { SortHeader, useOwnerSort } from "@/components/SortHeader";
import { toast } from "sonner";

const dt = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "-");
const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="text-xs font-medium text-muted-foreground">{label}</label>{children}</div>
);
const today = () => new Date().toISOString().slice(0, 10);

export default function Accounting() {
  const { company, selectedHub, hubs } = useApp();
  const isSwastik = (company?.name || "").toLowerCase().includes("swastik");
  const isDalmiaBulk = (hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase() === "dalmia bulk";
  const tabs = useMemo(() => [
    ...(isSwastik ? [] : [{ id: "owner", label: "Owner Advance" }]),
    { id: "ledger", label: "Owner Ledger" },
    { id: "voucher", label: "Voucher Book" },
    ...(isSwastik ? [] : [{ id: "cash", label: "Daily Cash Book" }]),
    { id: "recovery", label: "Recovery Register" },
    { id: "expense", label: "Expenditure" },
    ...(isDalmiaBulk ? [{ id: "adblue", label: "Adblue Register" }] : []),
    { id: "fastag", label: "FASTag" },
    { id: "bill", label: "Bill Register" },
  ], [isSwastik, isDalmiaBulk]);
  const [tab, setTab] = useState(tabs[0].id);
  useEffect(() => { if (!tabs.find((t) => t.id === tab)) setTab(tabs[0].id); }, [tabs, tab]);
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-6">Accounting</h1>
      <ModuleTabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "owner" && <OwnerAdvance />}
      {tab === "ledger" && <OwnerLedger />}
      {tab === "voucher" && <VoucherBook />}
      {tab === "cash" && <CashBook />}
      {tab === "recovery" && <RecoveryRegister />}
      {tab === "expense" && <Expenditure />}
      {tab === "adblue" && <AdblueRegister />}
      {tab === "fastag" && <Fastag />}
      {tab === "bill" && <BillRegister />}
    </div>
  );
}

function AdblueRegister() {
  const { selectedHub, hubs } = useApp();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { entry_date: today(), vehicle_number: "", quantity: "", amount: "", hub_id: "" };
  const [f, setF] = useState(empty);
  const [truckOcr, setTruckOcr] = useState(false);
  const [meterOcr, setMeterOcr] = useState(false);
  const [rates, setRates] = useState([]);
  const [rateOpen, setRateOpen] = useState(false);
  const load = useCallback(async () => setRows((await api.get("/adblue", { params: { hub: selectedHub } })).data), [selectedHub]);
  const loadRates = useCallback(async () => setRates((await api.get("/adblue-rates", { params: { hub: selectedHub } })).data), [selectedHub]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRates(); }, [loadRates]);
  useEffect(() => { api.get("/vehicles", { params: { hub: selectedHub } }).then((r) => setVehicles(r.data)); }, [selectedHub]);
  const effectiveRate = useCallback((date) => {
    const applicable = rates.filter((x) => (x.effective_date || "") <= (date || "")).sort((a, b) => (a.effective_date < b.effective_date ? 1 : -1));
    return applicable.length ? Number(applicable[0].rate) : 0;
  }, [rates]);
  const ownerOf = (vn) => (vehicles.find((v) => (v.vehicle_number || "").toUpperCase() === (vn || "").toUpperCase())?.owner_name) || "-";
  const filtered = rows.filter((r) => !q || (`${r.vehicle_number || ""} ${ownerOf(r.vehicle_number)}`).toLowerCase().includes(q.toLowerCase()));
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(filtered);
  const totalQty = filtered.reduce((s, r) => s + (r.quantity || 0), 0);
  const totalAmt = filtered.reduce((s, r) => s + (r.amount || 0), 0);
  const masterRate = effectiveRate(f.entry_date);
  const rate = masterRate > 0 ? masterRate : (Number(f.quantity) > 0 ? Number(f.amount || 0) / Number(f.quantity) : 0);
  const computedAmount = masterRate > 0 ? Number(((Number(f.quantity) || 0) * masterRate).toFixed(2)) : Number(f.amount || 0);
  const openCreate = () => { setEditing(null); setF({ ...empty, hub_id: selectedHub !== "all" ? selectedHub : hubs[0]?.id || "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r, quantity: String(r.quantity ?? ""), amount: String(r.amount ?? "") }); setOpen(true); };
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") });
  const scanTruck = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setTruckOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/adblue/truck-ocr`, fd, { headers: authHeaders() });
      if (data.vehicle_number) { setF((p) => ({ ...p, vehicle_number: data.vehicle_number })); toast.success(`Truck read: ${data.vehicle_number}`); }
      else toast.error("No number plate detected — enter manually");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the truck"); }
    finally { setTruckOcr(false); }
  };
  const scanMeter = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setMeterOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/adblue/meter-ocr`, fd, { headers: authHeaders() });
      if (data.quantity) { setF((p) => ({ ...p, quantity: String(data.quantity) })); toast.success(`Flow meter: ${data.quantity} L`); }
      else toast.error("No meter reading detected — enter manually");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the meter"); }
    finally { setMeterOcr(false); }
  };
  const save = async () => {
    if (!f.vehicle_number) return toast.error("Vehicle number required");
    const qn = Number(f.quantity) || 0;
    const mr = effectiveRate(f.entry_date);
    const an = mr > 0 ? Number((qn * mr).toFixed(2)) : (Number(f.amount) || 0);
    const rt = mr > 0 ? mr : (qn > 0 ? Number((an / qn).toFixed(2)) : 0);
    const payload = { ...f, quantity: qn, amount: an, rate: rt, hub_id: f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id) };
    if (editing) { await api.put(`/adblue/${editing}`, payload); toast.success("Adblue entry updated"); }
    else { await api.post("/adblue", payload); toast.success("Adblue entry added"); }
    setOpen(false); setF(empty); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/adblue/${id}`); toast.success("Deleted"); load(); };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Input className="w-64" placeholder="Search vehicle" value={q} onChange={(e) => setQ(e.target.value)} data-testid="adblue-search" />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setRateOpen(true)} data-testid="adblue-rate-master-button"><Banknote className="h-4 w-4 mr-1" /> Rate Master{masterRate > 0 ? ` · ₹${masterRate}/L` : ""}</Button>
          <Button onClick={openCreate} data-testid="add-adblue-button"><Plus className="h-4 w-4 mr-1" /> Add Adblue Entry</Button>
        </div>
      </div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="adblue-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-left">Vehicle Number</th><th className="py-3 px-4 text-left">Owner Name</th><th className="py-3 px-4 text-right">Adblue Qty (L)</th><th className="py-3 px-4 text-right">Amount</th><th className="py-3 px-4 text-right">Rate (₹/L)</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`adblue-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4">{dt(r.entry_date)}</td>
                <td className="py-3 px-4 font-mono">{r.vehicle_number}</td>
                <td className="py-3 px-4" data-testid={`adblue-owner-${r.id}`}>{ownerOf(r.vehicle_number)}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.quantity)}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.amount)}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.rate)}</td>
                <td className="py-3 px-4 text-center"><div className="flex items-center justify-center gap-3"><button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-adblue-${r.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-adblue-${r.id}`}><Trash2 className="h-4 w-4" /></button></div></td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={8} className="py-16 text-center text-muted-foreground">No Adblue entries</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4" colSpan={4}>Total ({filtered.length})</td><td className="py-3 px-4 text-right font-mono">{fmtNumber(totalQty)}</td><td className="py-3 px-4 text-right font-mono text-emerald-500">{fmtCurrency(totalAmt)}</td><td colSpan={2}></td></tr></tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="adblue-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Adblue Entry" : "Add Adblue Entry"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-3 flex flex-col gap-2" data-testid="adblue-truck-ocr-box">
              <div className="flex items-center gap-2 text-sm font-medium"><Truck className="h-4 w-4 text-primary" />Step 1 · Scan Truck</div>
              <div className="text-xs text-muted-foreground">Reads the number plate → Vehicle Number</div>
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={scanTruck} disabled={truckOcr} data-testid="adblue-truck-ocr-file" />
                <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90 w-fit" data-testid="adblue-truck-ocr-button"><ScanLine className="h-4 w-4" />{truckOcr ? "Reading…" : "Scan Truck"}</span>
              </label>
            </div>
            <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-3 flex flex-col gap-2" data-testid="adblue-meter-ocr-box">
              <div className="flex items-center gap-2 text-sm font-medium"><Gauge className="h-4 w-4 text-amber-500" />Step 2 · Scan Flow Meter</div>
              <div className="text-xs text-muted-foreground">Reads dispenser display → Adblue Qty (L)</div>
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={scanMeter} disabled={meterOcr} data-testid="adblue-meter-ocr-file" />
                <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90 w-fit" data-testid="adblue-meter-ocr-button"><ScanLine className="h-4 w-4" />{meterOcr ? "Reading…" : "Scan Meter"}</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><Input type="date" data-testid="adb-date-input" value={f.entry_date} onChange={(e) => setF({ ...f, entry_date: e.target.value })} /></Field>
            <Field label="Vehicle Number"><Combobox value={f.vehicle_number} onChange={(v) => setF({ ...f, vehicle_number: v })} options={vehicles.map((v) => v.vehicle_number)} placeholder="Type or select vehicle" testid="adb-vehicle-input" /></Field>
            <Field label="Adblue Qty (L) · Flow Meter"><Input type="number" data-testid="adb-qty-input" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
            {masterRate > 0 ? (
              <Field label={`Amount (₹) · auto @ ₹${masterRate}/L`}><Input value={computedAmount ? computedAmount.toFixed(2) : ""} disabled data-testid="adb-amount-input" placeholder="Qty × master rate" /></Field>
            ) : (
              <Field label="Amount (₹)"><Input type="number" data-testid="adb-amount-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
            )}
            <Field label={`Rate (${masterRate > 0 ? "master" : "auto"} ₹${rate ? rate.toFixed(2) : "0"}/L)`}><Input value={rate ? rate.toFixed(2) : ""} disabled placeholder={masterRate > 0 ? "From Rate Master" : "Auto from Qty & Amount"} data-testid="adb-rate-input" /></Field>
          </div>
          {masterRate === 0 && <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="adblue-no-rate-note">No rate set for this date in Rate Master — enter amount manually, or add a rate via "Rate Master".</p>}
          <DialogFooter><Button onClick={save} data-testid="save-adblue-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {rateOpen && <AdblueRateDialog rates={rates} onClose={() => setRateOpen(false)} onSaved={loadRates} />}
    </div>
  );
}

function AdblueRateDialog({ rates, onClose, onSaved }) {
  const { selectedHub, hubs } = useApp();
  const [f, setF] = useState({ effective_date: today(), rate: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!f.effective_date || !Number(f.rate)) return toast.error("Enter effective date and rate");
    setSaving(true);
    try {
      await api.post("/adblue-rates", { effective_date: f.effective_date, rate: Number(f.rate), notes: f.notes, hub_id: selectedHub !== "all" ? selectedHub : hubs[0]?.id });
      toast.success("Rate added");
      setF({ effective_date: today(), rate: "", notes: "" });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this rate?")) return; await api.delete(`/adblue-rates/${id}`); toast.success("Deleted"); onSaved(); };
  const sorted = [...rates].sort((a, b) => (a.effective_date < b.effective_date ? 1 : -1));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="adblue-rate-dialog">
        <DialogHeader><DialogTitle>AdBlue Rate Master (₹ / litre)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Each rate applies from its effective date until the next dated rate. Entry amounts auto-compute as Qty × applicable rate.</p>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <Field label="Effective From"><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="adb-rate-date" /></Field>
          <Field label="Rate (₹/L)"><Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="adb-rate-value" /></Field>
          <Button onClick={add} disabled={saving} data-testid="adb-rate-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
        <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
          <table className="w-full text-sm" data-testid="adblue-rate-table">
            <thead className="bg-muted/60 text-muted-foreground text-xs uppercase"><tr><th className="py-2 px-3 text-left">Effective From</th><th className="py-2 px-3 text-right">Rate (₹/L)</th><th className="py-2 px-3 text-center">Action</th></tr></thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`adb-rate-row-${r.id}`}>
                  <td className="py-2 px-3">{dt(r.effective_date)}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmtNumber(r.rate)}</td>
                  <td className="py-2 px-3 text-center"><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`adb-rate-del-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">No rates yet — add one above</td></tr>}
            </tbody>
          </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OwnerAdvance() {
  const { selectedHub, hubs } = useApp();
  const [rows, setRows] = useState([]);
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState("all");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const empty = { entry_date: today(), owner_name: "", outstanding_amount: "", hub_id: "" };
  const [f, setF] = useState(empty);

  const load = useCallback(async () => {
    const params = { hub: selectedHub };
    if (owner !== "all") params.owner = owner;
    setRows((await api.get("/owner-advances", { params })).data);
  }, [selectedHub, owner]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/owners", { params: { hub: selectedHub } }).then((r) => setOwners(r.data)); }, [selectedHub]);

  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(rows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);
  const total = rows.reduce((s, r) => s + (r.outstanding_amount || 0), 0);

  const save = async () => {
    if (!f.owner_name) return toast.error("Select owner");
    await api.post("/owner-advances", { ...f, outstanding_amount: Number(f.outstanding_amount) || 0, hub_id: f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id) });
    toast.success("Owner balance added"); setOpen(false); setF(empty); load();
  };
  const del = async (id) => { await api.delete(`/owner-advances/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger className="w-52" data-testid="owner-filter"><SelectValue placeholder="Select Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Owners</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="owner-apply">Apply</Button>
        </div>
        <Button onClick={() => { setF({ ...empty }); setOpen(true); }} data-testid="add-owner-balance-button"><Plus className="h-4 w-4 mr-1" /> Add Owner Balance</Button>
      </div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="owner-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 w-10"></th><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Date</th><SortHeader label="Owner Name" dir={osDir} onClick={osToggle} /><th className="py-3 px-4 text-right">Outstanding Amount</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <React.Fragment key={r.id}>
                <tr className="border-t border-border hover:bg-accent/40" data-testid={`owner-row-${r.id}`}>
                  <td className="py-3 px-4"><button onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`owner-expand-${r.id}`}><ChevronRight className={`h-4 w-4 transition-transform ${expanded === r.id ? "rotate-90" : ""}`} /></button></td>
                  <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                  <td className="py-3 px-4">{dt(r.entry_date)}</td>
                  <td className="py-3 px-4 font-medium">{r.owner_name}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.outstanding_amount)}</td>
                  <td className="py-3 px-4 text-center"><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-owner-adv-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-muted/30 border-t border-border"><td></td><td colSpan={5} className="py-3 px-4 text-xs text-muted-foreground">Entry Date: {dt(r.entry_date)} · Outstanding: {fmtCurrency(r.outstanding_amount)}</td></tr>
                )}
              </React.Fragment>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={6} className="py-16 text-center text-muted-foreground">No records</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      <div className="bg-muted/40 border border-border rounded-xl px-6 py-4 flex items-center justify-between" data-testid="owner-total">
        <span className="font-semibold">Total Outstanding Amount</span>
        <span className="font-mono font-bold text-emerald-500 text-lg">{fmtCurrency(total)}</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="owner-balance-dialog">
          <DialogHeader><DialogTitle>Add Owner Balance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Owner">
              <Select value={f.owner_name} onValueChange={(v) => setF({ ...f, owner_name: v })}>
                <SelectTrigger data-testid="ob-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date"><Input type="date" data-testid="ob-date-input" value={f.entry_date} onChange={(e) => setF({ ...f, entry_date: e.target.value })} /></Field>
            <Field label="Outstanding Amount"><Input type="number" data-testid="ob-amount-input" value={f.outstanding_amount} onChange={(e) => setF({ ...f, outstanding_amount: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-owner-balance-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OwnerLedger() {
  const { selectedHub } = useApp();
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owners", { params: { hub: selectedHub } }).then((r) => { setOwners(r.data); if (r.data[0]) setOwner(r.data[0].name); }); }, [selectedHub]);
  const load = useCallback(async () => { if (!owner) return; setData((await api.get("/owner-ledger", { params: { owner, hub: selectedHub } })).data); }, [owner, selectedHub]);
  useEffect(() => { load(); }, [load]);
  const s = data?.summary;
  const cards = [
    { label: "Advances", value: s?.advances, tone: "text-amber-500" },
    { label: "Vouchers Paid", value: s?.vouchers, tone: "text-primary" },
    { label: "Recovered", value: s?.recoveries, tone: "text-emerald-500" },
    { label: "Net Balance", value: s?.net_balance, tone: "text-rose-500" },
  ];
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger className="w-64" data-testid="ledger-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
          <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid={`ledger-card-${c.label.split(" ")[0].toLowerCase()}`}>
            <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
            <p className={`font-mono text-xl font-bold ${c.tone}`}>{fmtCurrency(c.value || 0)}</p>
          </div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-left">Type</th><th className="py-3 px-4 text-left">Narration</th><th className="py-3 px-4 text-right">Debit</th><th className="py-3 px-4 text-right">Credit</th><th className="py-3 px-4 text-right">Balance</th></tr>
          </thead>
          <tbody>
            {(data?.transactions || []).map((t, i) => (
              <tr key={i} className="border-t border-border hover:bg-accent/40" data-testid={`ledger-row-${i}`}>
                <td className="py-3 px-4">{dt(t.date)}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.type === "Recovery" ? "bg-emerald-500/15 text-emerald-500" : t.type === "Voucher" ? "bg-primary/15 text-primary" : "bg-amber-500/15 text-amber-500"}`}>{t.type}</span>
                </td>
                <td className="py-3 px-4 text-muted-foreground max-w-[280px] truncate">{t.narration}</td>
                <td className="py-3 px-4 text-right font-mono">{t.debit ? fmtCurrency(t.debit) : "-"}</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-500">{t.credit ? fmtCurrency(t.credit) : "-"}</td>
                <td className="py-3 px-4 text-right font-mono font-medium">{fmtCurrency(t.balance)}</td>
              </tr>
            ))}
            {(!data || data.transactions.length === 0) && <tr><td colSpan={6} className="py-16 text-center text-muted-foreground">No ledger entries</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VoucherBook() {
  const { selectedHub, hubs } = useApp();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("2026-09");
  const [open, setOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  const [importing, setImporting] = useState(false);
  const empty = { voucher_date: today(), vehicle_number: "", owner_name: "", owner_phone: "", narration: "", mode_of_payment: "Cash", amount: "", hub_id: "" };
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = { hub: selectedHub };
    if (month) params.month = month;
    if (q) params.q = q;
    setRows((await api.get("/vouchers", { params })).data);
  }, [selectedHub, month, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/vehicles", { params: { hub: selectedHub } }).then((r) => setVehicles(r.data));
    api.get("/owners", { params: { hub: selectedHub } }).then((r) => setOwners(r.data)).catch(() => setOwners([]));
  }, [selectedHub]);

  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(rows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  const onVehicle = (vn) => { const v = vehicles.find((x) => x.vehicle_number === vn); setF((p) => ({ ...p, vehicle_number: vn, owner_name: v?.owner_name || p.owner_name, owner_phone: v?.owner_phone || p.owner_phone })); };
  const onOwner = (name) => { const o = owners.find((x) => x.name === name); setF((p) => ({ ...p, owner_name: name, owner_phone: o?.phone || p.owner_phone })); };
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r, amount: String(r.amount ?? "") }); setOpen(true); };
  const save = async () => {
    if (!f.amount) return toast.error("Amount required");
    const payload = { ...f, amount: Number(f.amount) || 0, hub_id: f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id) };
    if (editing) { await api.put(`/vouchers/${editing}`, payload); toast.success("Voucher updated"); }
    else { await api.post("/vouchers", payload); toast.success("Voucher created"); }
    setOpen(false); setF(empty); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/vouchers/${id}`); toast.success("Deleted"); load(); };
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const hub = selectedHub !== "all" ? selectedHub : hubs[0]?.id;
    if (!hub) return toast.error("Select a hub first");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("hub", hub);
      const { data } = await axios.post(`${API}/vouchers/import`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") },
      });
      toast.success(`Imported ${data.created} voucher(s)${data.errors?.length ? ` · ${data.errors.length} rows skipped` : ""}`);
      if (data.errors?.length) console.warn("Voucher import issues:", data.errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Input className="w-64" placeholder="Search by Truck No / Owner Name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="voucher-search" />
          <MonthPicker value={month} onChange={setMonth} testid="voucher-month" />
          <Button variant="outline" onClick={load} data-testid="voucher-apply">Apply</Button>
        </div>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer" data-testid="voucher-import-label">
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} disabled={importing} data-testid="voucher-import-file" />
            <span className="inline-flex items-center gap-1.5 text-sm h-9 px-3 rounded-lg border border-border cursor-pointer hover:bg-muted/40 transition-colors" data-testid="voucher-import-button"><FileUp className="h-4 w-4" />{importing ? "Importing…" : "Import Excel"}</span>
          </label>
          <Button onClick={() => { setEditing(null); setF({ ...empty }); setOpen(true); }} data-testid="create-voucher-button"><Plus className="h-4 w-4 mr-1" /> Create Voucher</Button>
        </div>
      </div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="voucher-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Voucher Number</th><th className="py-3 px-4 text-left">Voucher Date</th><th className="py-3 px-4 text-left">Vehicle Number</th><SortHeader label="Owner Name" dir={osDir} onClick={osToggle} /><th className="py-3 px-4 text-left">Owner Phone</th><th className="py-3 px-4 text-left">Narration</th><th className="py-3 px-4 text-left">Mode of Payment</th><th className="py-3 px-4 text-right">Amount</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`voucher-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4 font-mono text-xs text-primary">{r.voucher_number}</td>
                <td className="py-3 px-4">{dt(r.voucher_date)}</td>
                <td className="py-3 px-4 font-mono">{r.vehicle_number}</td>
                <td className="py-3 px-4">{r.owner_name}</td>
                <td className="py-3 px-4 font-mono text-xs">{r.owner_phone}</td>
                <td className="py-3 px-4 max-w-[160px] truncate">{r.narration}</td>
                <td className="py-3 px-4">{r.mode_of_payment}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.amount)}</td>
                <td className="py-3 px-4 text-center"><div className="flex items-center justify-center gap-3"><button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-voucher-${r.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-voucher-${r.id}`}><Trash2 className="h-4 w-4" /></button></div></td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={10} className="py-16 text-center text-muted-foreground">No data</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4" colSpan={8}>Total Amount</td><td className="py-3 px-4 text-right font-mono text-emerald-500">{fmtCurrency(total)}</td><td></td></tr></tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="voucher-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Voucher" : "Create Voucher"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Voucher Date"><Input type="date" data-testid="vch-date-input" value={f.voucher_date} onChange={(e) => setF({ ...f, voucher_date: e.target.value })} /></Field>
            <Field label="Vehicle Number (optional)">
              <Select value={f.vehicle_number} onValueChange={onVehicle}>
                <SelectTrigger data-testid="vch-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.vehicle_number}>{v.vehicle_number}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Select Owner">
              <Select value={owners.some((o) => o.name === f.owner_name) ? f.owner_name : ""} onValueChange={onOwner}>
                <SelectTrigger data-testid="vch-owner-select"><SelectValue placeholder="Pick owner" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Owner Name"><Input data-testid="vch-owner-input" value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} /></Field>
            <Field label="Owner Phone"><Input data-testid="vch-phone-input" value={f.owner_phone} onChange={(e) => setF({ ...f, owner_phone: e.target.value })} /></Field>
            <Field label="Mode of Payment">
              <Select value={f.mode_of_payment} onValueChange={(v) => setF({ ...f, mode_of_payment: v })}>
                <SelectTrigger data-testid="vch-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank Transfer", "UPI", "Cheque"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount"><Input type="number" data-testid="vch-amount-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Narration"><Input data-testid="vch-narration-input" value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })} /></Field></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-voucher-button">Save Voucher</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CashBook() {
  const { selectedHub } = useApp();
  const [data, setData] = useState({ rows: [], total_in: 0, total_out: 0 });
  const [month, setMonth] = useState("2026-09");
  const load = useCallback(async () => {
    const params = { hub: selectedHub };
    if (month) params.month = month;
    setData((await api.get("/cashbook", { params })).data);
  }, [selectedHub, month]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <MonthPicker value={month} onChange={setMonth} testid="cash-month" />
        <Button variant="outline" onClick={load} data-testid="cash-apply">Apply</Button>
      </div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="cashbook-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-right">Cash In</th><th className="py-3 px-4 text-right">Cash Out</th><th className="py-3 px-4 text-right">Balance</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.date} className="border-t border-border hover:bg-accent/40" data-testid={`cash-row-${r.date}`}>
                <td className="py-3 px-4">{dt(r.date)}</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-500">{r.cash_in ? fmtCurrency(r.cash_in) : "-"}</td>
                <td className="py-3 px-4 text-right font-mono text-rose-500">{r.cash_out ? fmtCurrency(r.cash_out) : "-"}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.balance)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={4} className="py-16 text-center text-muted-foreground">No cash movements</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4">Total</td><td className="py-3 px-4 text-right font-mono text-emerald-500">{fmtCurrency(data.total_in)}</td><td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrency(data.total_out)}</td><td className="py-3 px-4 text-right font-mono">{fmtCurrency(data.total_in - data.total_out)}</td></tr></tfoot>
        </table>
      </div>
    </div>
  );
}

function RecoveryRegister() {
  const { selectedHub, hubs } = useApp();
  const [rows, setRows] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { recovery_date: today(), owner_name: "", amount: "", mode_of_payment: "Cash", notes: "", hub_id: "" };
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    let data = (await api.get("/recoveries", { params: { hub: selectedHub } })).data;
    data.sort((a, b) => (b.recovery_date || "").localeCompare(a.recovery_date || ""));
    setRows(data);
  }, [selectedHub]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/owners", { params: { hub: selectedHub } }).then((r) => setOwners(r.data)); }, [selectedHub]);
  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(rows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r, amount: String(r.amount ?? "") }); setOpen(true); };
  const del = async (id) => { await api.delete(`/recoveries/${id}`); toast.success("Deleted"); load(); };
  const save = async () => {
    if (!f.owner_name || !f.amount) return toast.error("Owner & amount required");
    const payload = { ...f, amount: Number(f.amount) || 0, hub_id: f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id) };
    if (editing) { await api.put(`/recoveries/${editing}`, payload); toast.success("Recovery updated"); }
    else { await api.post("/recoveries", payload); toast.success("Recovery added"); }
    setOpen(false); setF(empty); setEditing(null); load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button onClick={() => { setEditing(null); setF({ ...empty }); setOpen(true); }} data-testid="add-recovery-button"><Plus className="h-4 w-4 mr-1" /> Add Recovery</Button>
      </div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="recovery-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Date</th><SortHeader label="Owner Name" dir={osDir} onClick={osToggle} /><th className="py-3 px-4 text-left">Mode</th><th className="py-3 px-4 text-left">Notes</th><th className="py-3 px-4 text-right">Amount</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`recovery-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4">{dt(r.recovery_date)}</td>
                <td className="py-3 px-4 font-medium">{r.owner_name}</td>
                <td className="py-3 px-4">{r.mode_of_payment}</td>
                <td className="py-3 px-4 text-muted-foreground">{r.notes || "-"}</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-500">{fmtCurrency(r.amount)}</td>
                <td className="py-3 px-4 text-center"><div className="flex items-center justify-center gap-3"><button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-recovery-${r.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-recovery-${r.id}`}><Trash2 className="h-4 w-4" /></button></div></td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={7} className="py-16 text-center text-muted-foreground">No recoveries</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4" colSpan={6}>Total Recovered</td><td className="py-3 px-4 text-right font-mono text-emerald-500">{fmtCurrency(total)}</td></tr></tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="recovery-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Recovery" : "Add Recovery"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><Input type="date" data-testid="rec-date-input" value={f.recovery_date} onChange={(e) => setF({ ...f, recovery_date: e.target.value })} /></Field>
            <Field label="Owner">
              <Select value={f.owner_name} onValueChange={(v) => setF({ ...f, owner_name: v })}>
                <SelectTrigger data-testid="rec-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount"><Input type="number" data-testid="rec-amount-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
            <Field label="Mode">
              <Select value={f.mode_of_payment} onValueChange={(v) => setF({ ...f, mode_of_payment: v })}>
                <SelectTrigger data-testid="rec-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank Transfer", "UPI"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="col-span-2"><Field label="Notes"><Input data-testid="rec-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-recovery-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BillRegister() {
  const { selectedHub } = useApp();
  const [bills, setBills] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [payBill, setPayBill] = useState(null);
  const [payScanOpen, setPayScanOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const load = useCallback(async () => {
    const params = { hub: selectedHub };
    if (q) params.q = q;
    setBills((await api.get("/bills-register", { params })).data);
  }, [selectedHub, q]);
  useEffect(() => { load(); }, [load]);
  const [pendingRet, setPendingRet] = useState(false);
  const [retDays, setRetDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState("all");
  const pendingCount = bills.filter((b) => b.match_status === "pending").length;
  const recordedCount = bills.length - pendingCount;
  const shown = bills
    .filter((b) => statusFilter === "all" || b.match_status === statusFilter)
    .filter((b) => !pendingRet || ((b.gst_retained || 0) > 0 && !b.gst_released));
  const { sorted: sortedBills, dir: bnDir, toggle: bnToggle } = useOwnerSort(shown, "bill_number");
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(sortedBills);
  const bt = shown.reduce((a, r) => ({
    value_raised: a.value_raised + (r.value_raised || 0),
    value_received: a.value_received + (r.value_received || 0),
    tax: a.tax + (r.tax || 0),
    gst_amount: a.gst_amount + (r.gst_amount || 0),
    cgst: a.cgst + (r.cgst || 0), sgst: a.sgst + (r.sgst || 0), igst: a.igst + (r.igst || 0),
    gst_retained: a.gst_retained + (r.gst_retained || 0),
    difference: a.difference + (r.difference || 0),
  }), { value_raised: 0, value_received: 0, tax: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, gst_retained: 0, difference: 0 });
  const del = async (id) => { await api.delete(`/bills-register/${id}`); toast.success("Bill deleted"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input className="w-56" placeholder="Search bill number" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bill-search" />
        <div className="flex items-center gap-1.5" data-testid="bill-status-filter">
          {[{ id: "all", label: `All · ${bills.length}` }, { id: "pending", label: `Pending · ${pendingCount}` }, { id: "recorded", label: `Recorded · ${recordedCount}` }].map((c) => (
            <button key={c.id} onClick={() => setStatusFilter(c.id)} data-testid={`bill-filter-${c.id}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === c.id
                ? c.id === "pending" ? "border-rose-500/50 bg-rose-500/15 text-rose-500 font-medium"
                  : c.id === "recorded" ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-500 font-medium"
                    : "border-primary/50 bg-primary/15 text-primary font-medium"
                : "border-border text-muted-foreground hover:bg-accent"}`}>{c.label}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="retention-filter-label"><input type="checkbox" checked={pendingRet} onChange={(e) => setPendingRet(e.target.checked)} data-testid="retention-filter" />Pending GST retention</label>
        <label className="flex items-center gap-1.5 text-sm" data-testid="retention-days-label">Flag after <Input className="w-16 h-8" type="number" value={retDays} onChange={(e) => setRetDays(e.target.value)} data-testid="retention-days" /> days</label>
        <div className="ml-auto flex items-center gap-2">
          <PageSize value={pageSize} onChange={setPageSize} />
          <Button variant="outline" onClick={() => setPayScanOpen(true)} data-testid="record-payments-button"><Banknote className="h-4 w-4 mr-1.5" />Record Payments</Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-import-button"><FileUp className="h-4 w-4 mr-1.5" />Bulk Import</Button>
          <Button onClick={() => { setEditBill(null); setOpen(true); }} data-testid="create-bill-button"><Plus className="h-4 w-4 mr-1.5" />Create Bill</Button>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="bill-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="py-3 px-4 w-8"></th><SortHeader label="Bill Number" dir={bnDir} onClick={bnToggle} testid="sort-bill-number" /><th className="py-3 px-4 text-left">Bill Date</th><th className="py-3 px-4 text-left">Type</th><th className="py-3 px-4 text-left">Status</th>
              <th className="py-3 px-4 text-right">Value Raised</th><th className="py-3 px-4 text-right">Value Received</th>
              <th className="py-3 px-4 text-left">Received Date</th>
              <th className="py-3 px-4 text-right">TDS</th>
              <th className="py-3 px-4 text-right">GST %</th><th className="py-3 px-4 text-right">GST</th>
              <th className="py-3 px-4 text-right">CGST</th><th className="py-3 px-4 text-right">SGST</th><th className="py-3 px-4 text-right">IGST</th>
              <th className="py-3 px-4 text-center">GST Retention</th>
              <th className="py-3 px-4 text-right">Difference</th>
              <th className="py-3 px-4 text-left">Remarks</th><th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <React.Fragment key={r.id}>
                <tr className={`border-t border-border ${r.match_status === "pending" ? "bg-rose-500/[0.06]" : (i % 2 ? "row-even" : "row-odd")}`} data-testid={`bill-row-${i}`}>
                  <td className="py-3 px-4"><button onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))} data-testid={`bill-expand-${i}`}><ChevronRight className={`h-4 w-4 transition-transform ${expanded[r.id] ? "rotate-90" : ""}`} /></button></td>
                  <td className="py-3 px-4 font-mono text-xs text-primary">{r.bill_number}</td>
                  <td className="py-3 px-4">{r.bill_date ? dt(r.bill_date) : "-"}</td>
                  <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">{r.bill_type}</span></td>
                  <td className="py-3 px-4" data-testid={`bill-status-${i}`}>{r.match_status === "recorded"
                    ? <span className="text-xs px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 whitespace-nowrap">Recorded</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full border border-rose-500/50 bg-rose-500/15 text-rose-500 whitespace-nowrap">Pending{r.dn_count ? ` · ${r.matched_count}/${r.dn_count}` : ""}</span>}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.value_raised)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.value_received)}</td>
                  <td className="py-3 px-4">{r.received_date ? dt(r.received_date) : "-"}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.tax)}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.gst_enabled ? `${fmtNumber(r.gst_pct)}%` : "-"}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.gst_enabled ? fmtCurrency(r.gst_amount) : "-"}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.cgst ? fmtCurrency(r.cgst) : "-"}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.sgst ? fmtCurrency(r.sgst) : "-"}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.igst ? fmtCurrency(r.igst) : "-"}</td>
                  <td className="py-3 px-4 text-center">{r.gst_retained ? (() => {
                    const base = r.received_date || r.bill_date || r.created_at;
                    const days = base ? Math.floor((Date.now() - new Date(base).getTime()) / 86400000) : 0;
                    const overdue = !r.gst_released && Number(retDays) >= 0 && days > Number(retDays);
                    const cls = r.gst_released ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : overdue ? "border-rose-500/50 bg-rose-500/15 text-rose-500" : "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400";
                    return <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`} data-testid={`bill-retention-cell-${i}`}>{fmtCurrency(r.gst_retained)} · {r.gst_released ? "Released" : overdue ? `Overdue ${days}d` : "Pending"}</span>;
                  })() : <span className="text-muted-foreground">-</span>}</td>
                  <td className={`py-3 px-4 text-right font-mono ${r.difference < 0 ? "text-rose-500" : "text-emerald-500"}`}>{fmtCurrency(r.difference)}</td>
                  <td className="py-3 px-4 text-muted-foreground max-w-[160px] truncate">{r.remarks || "-"}</td>
                  <td className="py-2 px-3 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 font-medium" onClick={() => { setEditBill(r); setOpen(true); }} data-testid={`bill-edit-${i}`} title="Edit bill"><Pencil className="h-3.5 w-3.5" />Edit</Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`bill-actions-${i}`} title="More actions"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Bill Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPayBill(r)} className="gap-2.5 cursor-pointer" data-testid={`bill-payment-${i}`}>
                            <span className="h-6 w-6 rounded-md bg-emerald-500/10 inline-flex items-center justify-center shrink-0"><Banknote className="h-3.5 w-3.5 text-emerald-500" /></span>Record Payment
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => del(r.id)} className="gap-2.5 cursor-pointer text-rose-500 focus:text-rose-500 focus:bg-rose-500/10" data-testid={`bill-delete-${i}`}>
                            <span className="h-6 w-6 rounded-md bg-rose-500/10 inline-flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></span>Delete Bill
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                {expanded[r.id] && (
                  <tr className="bg-muted/30"><td colSpan={18} className="px-6 py-3">
                    <table className="w-full text-xs" data-testid={`bill-items-${i}`}>
                      <thead className="text-muted-foreground uppercase"><tr><th className="py-1.5 text-left">Invoice Date</th><th className="py-1.5 text-left">Truck Number</th><th className="py-1.5 text-left">Delivery Number</th><th className="py-1.5 text-right">Qty</th><th className="py-1.5 text-right">Rate</th><th className="py-1.5 text-right">Total</th></tr></thead>
                      <tbody>
                        {r.line_items.map((it, j) => (
                          it.matched === false ? (
                            <tr key={j} className="border-t border-border/60 bg-rose-500/[0.05]" data-testid={`bill-item-pending-${i}-${j}`}>
                              <td className="py-1.5 text-rose-500/80 italic" colSpan={2}>Awaiting DO in Receive Register</td>
                              <td className="py-1.5 font-mono text-rose-500">{it.delivery_number}</td>
                              <td className="py-1.5 text-right font-mono text-muted-foreground">-</td>
                              <td className="py-1.5 text-right font-mono">{it.rate ? fmtNumber(it.rate) : "-"}</td>
                              <td className="py-1.5 text-right font-mono text-muted-foreground">-</td>
                            </tr>
                          ) : (
                            <tr key={j} className="border-t border-border/60"><td className="py-1.5">{dt(it.invoice_date)}</td><td className="py-1.5 font-mono">{it.vehicle_number}</td><td className="py-1.5 font-mono"><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle"></span>{it.delivery_number}</td><td className="py-1.5 text-right font-mono">{fmtNumber(it.quantity)}</td><td className="py-1.5 text-right font-mono">{fmtNumber(it.rate)}</td><td className="py-1.5 text-right font-mono">{fmtCurrency(it.total)}</td></tr>
                          )
                        ))}
                        {r.line_items.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">No linked deliveries</td></tr>}
                      </tbody>
                    </table>
                    {(r.cgst || r.sgst || r.igst) ? (
                      <div className="mt-2 flex flex-wrap gap-4 text-xs" data-testid={`bill-gst-breakdown-${i}`}>
                        <span>CGST: <span className="font-mono">{fmtCurrency(r.cgst)}</span></span>
                        <span>SGST: <span className="font-mono">{fmtCurrency(r.sgst)}</span></span>
                        <span>IGST: <span className="font-mono">{fmtCurrency(r.igst)}</span></span>
                        <span className="font-semibold">Total GST: <span className="font-mono">{fmtCurrency((r.cgst || 0) + (r.sgst || 0) + (r.igst || 0))}</span></span>
                      </div>
                    ) : null}
                    {r.gst_retained ? (
                      <div className="mt-1 text-xs" data-testid={`bill-gst-retention-${i}`}>GST Retained: <span className="font-mono">{fmtCurrency(r.gst_retained)}</span> — {r.gst_released ? <span className="text-emerald-500">Released{r.gst_release_date ? ` on ${dt(r.gst_release_date)}` : ""}{r.gst_release_note ? ` · ${r.gst_release_note}` : ""}</span> : <span className="text-amber-500">Retained (pending release)</span>}</div>
                    ) : null}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={18} className="py-16 text-center text-muted-foreground">No bills yet</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/50 font-semibold" data-testid="bill-totals-row">
              <td className="py-3 px-4" colSpan={5}>Totals · {shown.length} bills</td>
              <td className="py-3 px-4 text-right font-mono" data-testid="bill-total-raised">{fmtCurrency(bt.value_raised)}</td>
              <td className="py-3 px-4 text-right font-mono" data-testid="bill-total-received">{fmtCurrency(bt.value_received)}</td>
              <td className="py-3 px-4"></td>
              <td className="py-3 px-4 text-right font-mono" data-testid="bill-total-tds">{fmtCurrency(bt.tax)}</td>
              <td className="py-3 px-4"></td>
              <td className="py-3 px-4 text-right font-mono" data-testid="bill-total-gst">{fmtCurrency(bt.gst_amount)}</td>
              <td className="py-3 px-4 text-right font-mono">{fmtCurrency(bt.cgst)}</td>
              <td className="py-3 px-4 text-right font-mono">{fmtCurrency(bt.sgst)}</td>
              <td className="py-3 px-4 text-right font-mono">{fmtCurrency(bt.igst)}</td>
              <td className="py-3 px-4 text-center font-mono">{fmtCurrency(bt.gst_retained)}</td>
              <td className={`py-3 px-4 text-right font-mono ${bt.difference < 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid="bill-total-difference">{fmtCurrency(bt.difference)}</td>
              <td className="py-3 px-4"></td>
              <td className="py-3 px-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
      {open && <CreateBill bill={editBill} onClose={() => { setOpen(false); setEditBill(null); }} onSaved={() => { setOpen(false); setEditBill(null); load(); }} />}
      {bulkOpen && <BulkImport onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      {payBill && <RecordPayment bill={payBill} onClose={() => setPayBill(null)} onSaved={() => { setPayBill(null); load(); }} />}
      {payScanOpen && <RecordPaymentsBulk onClose={() => setPayScanOpen(false)} onDone={() => { setPayScanOpen(false); load(); }} />}
    </div>
  );
}

function CreateBill({ onClose, onSaved, bill }) {
  const { selectedHub } = useApp();
  const [avail, setAvail] = useState([]);
  const [selected, setSelected] = useState(bill ? (bill.line_items || []).map((it) => ({ delivery_number: it.delivery_number, vehicle_number: it.vehicle_number, quantity: it.quantity, rate: it.rate })) : []);
  const [search, setSearch] = useState("");
  const [f, setF] = useState(bill
    ? { bill_number: bill.bill_number || "", bill_type: bill.bill_type || "EPOD", bill_date: bill.bill_date || "", value_raised: String(bill.value_raised ?? ""), value_received: String(bill.value_received ?? ""), tax: String(bill.tax ?? ""), gst_enabled: !!bill.gst_enabled, gst_pct: String(bill.gst_pct ?? ""), gst_amount: String(bill.gst_amount ?? ""), remarks: bill.remarks || "" }
    : { bill_number: "", bill_type: "EPOD", bill_date: "", value_raised: "", value_received: "", tax: "", gst_enabled: false, gst_pct: "", gst_amount: "", remarks: "" });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrGst, setOcrGst] = useState(null);
  useEffect(() => {
    api.get("/bills-register/available", { params: { hub: selectedHub } }).then((r) => {
      let a = r.data;
      if (bill) {
        const have = new Set(a.map((x) => x.delivery_number));
        const extra = (bill.line_items || []).filter((it) => !have.has(it.delivery_number)).map((it) => ({ delivery_number: it.delivery_number, vehicle_number: it.vehicle_number, quantity: it.quantity, rate: it.rate }));
        a = [...extra, ...a];
      }
      setAvail(a);
    });
  }, [selectedHub, bill]);

  const selDn = new Set(selected.map((s) => s.delivery_number));
  const filtered = avail.filter((a) => !selDn.has(a.delivery_number) && (!search || a.delivery_number.includes(search) || (a.vehicle_number || "").toLowerCase().includes(search.toLowerCase())));
  const addOne = (a) => setSelected((p) => [...p, a]);
  const removeOne = (a) => setSelected((p) => p.filter((x) => x.delivery_number !== a.delivery_number));
  const [manualDn, setManualDn] = useState("");
  const addManual = () => {
    const dn = manualDn.trim();
    if (!dn) return;
    if (selDn.has(dn)) return toast.error("Already added");
    setSelected((p) => [...p, { delivery_number: dn, vehicle_number: "", quantity: 0, rate: 0, unmatched: true }]);
    setManualDn("");
  };
  const setSelRate = (dn, v) => setSelected((p) => p.map((s) => (s.delivery_number === dn ? { ...s, rate: v === "" ? "" : Number(v) } : s)));
  const autoRaised = selected.reduce((s, x) => s + (x.quantity || 0) * (x.rate || 0), 0);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tl_token");
      const cid = localStorage.getItem("tl_company");
      const { data } = await axios.post(`${API}/bills-register/ocr?hub=${selectedHub || ""}`, fd, { headers: { Authorization: `Bearer ${token}`, "X-Company-Id": cid } });
      setF((p) => ({ ...p, bill_number: data.bill_number || p.bill_number, bill_date: data.bill_date || p.bill_date }));
      const rates = data.delivery_rates || {};
      const availMap = new Map(avail.map((a) => [String(a.delivery_number), a]));
      const have = new Set(selected.map((s) => String(s.delivery_number)));
      const toAdd = (data.delivery_numbers || data.matched || []).map(String).filter((d) => d && !have.has(d))
        .map((d) => availMap.get(d) || { delivery_number: d, vehicle_number: "", quantity: 0, rate: 0, unmatched: true });
      const nextSelected = [...selected, ...toAdd].map((s) => (rates[s.delivery_number] ? { ...s, rate: Number(rates[s.delivery_number]) } : s));
      setSelected(nextSelected);
      const cgst = Number(data.cgst) || 0, sgst = Number(data.sgst) || 0, igst = Number(data.igst) || 0;
      const gstTotal = Number(data.gst_total) || (cgst + sgst + igst);
      const raised = nextSelected.reduce((s, x) => s + (x.quantity || 0) * (x.rate || 0), 0);
      const amounts = data.delivery_amounts || {};
      const ocrTotal = Number(data.total_value) || Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);
      if (ocrTotal > 0) setF((p) => ({ ...p, value_raised: String(Math.round(ocrTotal)) }));
      if (gstTotal > 0) {
        setOcrGst({ cgst, sgst, igst });
        const base = ocrTotal || raised;
        const pct = Number(data.gst_percent) || (base > 0 ? Math.round((gstTotal / base) * 100) : 0);
        setF((p) => ({ ...p, gst_enabled: true, gst_amount: String(Math.round(gstTotal)), gst_pct: pct ? String(pct) : p.gst_pct }));
      }
      toast.success(`AI read the bill · ${(data.matched || []).length} delivery no. matched${(data.unmatched || []).length ? `, ${data.unmatched.length} not found` : ""}${ocrTotal > 0 ? ` · Value ₹${Math.round(ocrTotal)}` : ""}${gstTotal > 0 ? ` · GST ₹${Math.round(gstTotal)}` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the document");
    } finally {
      setOcrLoading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!f.bill_number.trim()) return toast.error("Bill Number is required");
    if (selected.length === 0) return toast.error("Select at least one delivery number");
    const payload = {
      bill_number: f.bill_number.trim(), bill_type: f.bill_type, bill_date: f.bill_date,
      value_raised: Number(f.value_raised || autoRaised), value_received: Number(f.value_received || 0),
      tax: Number(f.tax || 0), remarks: f.remarks,
      gst_enabled: f.gst_enabled, gst_pct: Number(f.gst_pct || 0), gst_amount: Number(f.gst_amount || 0),
      cgst: ocrGst?.cgst ?? bill?.cgst ?? 0, sgst: ocrGst?.sgst ?? bill?.sgst ?? 0, igst: ocrGst?.igst ?? bill?.igst ?? 0,
      gst_retained: bill?.gst_retained || 0, gst_released: bill?.gst_released || false, gst_release_note: bill?.gst_release_note || "", gst_release_date: bill?.gst_release_date || "",
      delivery_numbers: selected.map((s) => s.delivery_number),
      delivery_rates: selected.reduce((acc, s) => { const r = Number(s.rate); if (r > 0) acc[s.delivery_number] = r; return acc; }, {}),
      hub_id: selectedHub && selectedHub !== "all" ? selectedHub : "",
    };
    try {
      if (bill) { await api.put(`/bills-register/${bill.id}`, payload); toast.success("Bill updated"); }
      else { await api.post("/bills-register", payload); toast.success("Bill created"); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save bill"); }
  };

  const List = ({ title, items, action, testid, label, editable, onRate }) => (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="px-3 py-2 bg-muted/60 text-xs font-semibold uppercase text-muted-foreground">{title} ({items.length})</div>
      <div className="max-h-64 overflow-y-auto divide-y divide-border" data-testid={testid}>
        {items.map((a) => (
          <div key={a.delivery_number} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            <div className="min-w-0"><div className="font-mono text-xs truncate">{a.delivery_number}</div><div className="text-[11px] text-muted-foreground">{a.unmatched ? <span className="text-amber-500 font-medium">Not in Receive Register — auto-matches when posted</span> : <>{a.vehicle_number} · {fmtNumber(a.quantity)}T</>}</div></div>
            <div className="flex items-center gap-2">
              {editable && <Input type="number" className="h-8 w-24 text-right" value={a.rate ?? ""} placeholder="Rate/ton" onChange={(e) => onRate(a.delivery_number, e.target.value)} data-testid={`bill-selected-rate-${a.delivery_number}`} />}
              <Button size="sm" variant="outline" onClick={() => action(a)}>{label}</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="px-3 py-8 text-center text-xs text-muted-foreground">Empty</div>}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="create-bill-dialog">
        <DialogHeader><DialogTitle>{bill ? "Edit Bill" : "Create Bill"}</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 flex items-center gap-3" data-testid="bill-ocr-box">
          <FileUp className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Auto-fill from PDF</div>
            <div className="text-xs text-muted-foreground">Upload a bill PDF — AI reads Bill No, Bill Date & Delivery Numbers</div>
          </div>
          <label className="ml-auto shrink-0">
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFile} disabled={ocrLoading} data-testid="bill-ocr-file" />
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">{ocrLoading ? "Reading…" : "Upload PDF"}</span>
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Bill Number *"><Input value={f.bill_number} onChange={(e) => setF({ ...f, bill_number: e.target.value })} data-testid="bill-number-input" /></Field>
          <Field label="Bill Date"><Input type="date" value={f.bill_date} onChange={(e) => setF({ ...f, bill_date: e.target.value })} data-testid="bill-date-input" /></Field>
          <Field label="Bill Type *">
            <Select value={f.bill_type} onValueChange={(v) => setF({ ...f, bill_type: v })}>
              <SelectTrigger data-testid="bill-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["EPOD", "Manual"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="TDS"><Input type="number" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} data-testid="bill-tax-input" /></Field>
          <Field label={`Value Raised (auto ${fmtCurrency(autoRaised)})`}><Input type="number" value={f.value_raised} onChange={(e) => setF({ ...f, value_raised: e.target.value })} data-testid="bill-raised-input" placeholder={String(Math.round(autoRaised))} /></Field>
          <Field label="Value Received"><Input type="number" value={f.value_received} onChange={(e) => setF({ ...f, value_received: e.target.value })} data-testid="bill-received-input" /></Field>
          <Field label="Remarks"><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="bill-remarks-input" /></Field>
        </div>
        <div className="flex items-center gap-3 py-1">
          <Switch checked={f.gst_enabled} onCheckedChange={(v) => setF({ ...f, gst_enabled: v })} data-testid="bill-gst-toggle" />
          <span className="text-sm font-medium">Invoice contains GST</span>
        </div>
        {f.gst_enabled && (
          <div className="grid grid-cols-2 gap-4" data-testid="bill-gst-fields">
            <Field label="GST %"><Input type="number" value={f.gst_pct} onChange={(e) => { const pct = e.target.value; setF((p) => ({ ...p, gst_pct: pct, gst_amount: String(Math.round(((Number(p.value_raised || autoRaised)) * (Number(pct) || 0)) / 100)) })); }} data-testid="bill-gst-pct-input" /></Field>
            <Field label="GST Amount"><Input type="number" value={f.gst_amount} onChange={(e) => setF({ ...f, gst_amount: e.target.value })} data-testid="bill-gst-amount-input" /></Field>
          </div>
        )}
        <Input className="mt-2" placeholder="Search available delivery numbers" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bill-available-search" />
        <div className="flex gap-2 mt-2">
          <Input placeholder="Type a delivery number not in the list (DO not posted yet)" value={manualDn} onChange={(e) => setManualDn(e.target.value)} data-testid="bill-manual-dn" />
          <Button variant="outline" onClick={addManual} data-testid="bill-manual-dn-add">Add</Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <List title="Available Delivery Numbers" items={filtered} action={addOne} testid="bill-available-list" label="Add" />
          <List title="Selected Delivery Numbers" items={selected} action={removeOne} testid="bill-selected-list" label="Remove" editable onRate={setSelRate} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-bill-button">{bill ? "Update Bill" : "Create Bill"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BulkImport({ onClose, onDone }) {
  const { selectedHub, hubs } = useApp();
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState(null);   // review rows after preview
  const [avail, setAvail] = useState([]);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState(null);
  const onPick = (e) => setFiles(Array.from(e.target.files || []));

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") });

  const scan = async () => {
    if (!files.length) return toast.error("Select at least one PDF");
    setScanning(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/bills-register/bulk-preview?hub=${selectedHub || ""}`, fd, { headers: authHeaders() });
      setAvail(data.available || []);
      const rows = (data.items || []).map((it) => {
        const cg = Number(it.cgst) || 0, sg = Number(it.sgst) || 0, ig = Number(it.igst) || 0;
        const total = Number(it.gst_total) || 0;
        const igst = (cg + sg + ig) === 0 && total > 0 ? total : ig;  // fallback: combined GST total → IGST
        return {
          ...it, cgst: cg, sgst: sg, igst, value_raised: Number(it.value_raised) || 0,
          selected: [...(it.delivery_numbers || [])],   // keep every extracted DN — unmatched ones link automatically once posted
          skip: !!it.error || !it.bill_number,
        };
      });
      setItems(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan the files");
    } finally {
      setScanning(false);
    }
  };

  const setRow = (i, patch) => setItems((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const toggleDn = (i, dn) => setItems((p) => p.map((r, j) => (j === i ? { ...r, selected: r.selected.includes(dn) ? r.selected.filter((x) => x !== dn) : [...r.selected, dn] } : r)));
  const availSet = new Set(avail.map((a) => String(a.delivery_number)));
  const availNumbers = avail.map((a) => String(a.delivery_number));
  const addDn = (i) => setItems((p) => p.map((r, j) => {
    if (j !== i) return r;
    const dn = (r._add || "").trim();
    if (!dn) return r;
    return { ...r, delivery_numbers: r.delivery_numbers.includes(dn) ? r.delivery_numbers : [...r.delivery_numbers, dn], selected: r.selected.includes(dn) ? r.selected : [...r.selected, dn], _add: "" };
  }));

  const readyCount = (items || []).filter((r) => !r.skip && r.bill_number && !r.duplicate).length;

  const finalize = async () => {
    const bills = items.filter((r) => !r.skip && r.bill_number && !r.duplicate).map((r) => ({
      bill_number: r.bill_number.trim(), bill_date: r.bill_date, bill_type: "EPOD", delivery_numbers: r.selected,
      cgst: Number(r.cgst) || 0, sgst: Number(r.sgst) || 0, igst: Number(r.igst) || 0, value_raised: Number(r.value_raised) || 0,
      delivery_rates: r.delivery_rates || {},
    }));
    if (!bills.length) return toast.error("No bills ready to import — fix the flagged rows first");
    setCreating(true);
    try {
      const hub = selectedHub && selectedHub !== "all" ? selectedHub : (hubs[0]?.id || "");
      const { data } = await axios.post(`${API}/bills-register/bulk-create`, { bills, hub_id: hub }, { headers: { ...authHeaders(), "Content-Type": "application/json" } });
      setResults(data);
      toast.success(`Created ${data.created} bill(s)${data.failed ? `, ${data.failed} skipped` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-bill-dialog">
        <DialogHeader><DialogTitle>Bulk Import Bills</DialogTitle></DialogHeader>

        {/* Step 1: choose + scan */}
        {!items && !results && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-5 text-center">
            <FileUp className="h-6 w-6 text-primary mx-auto mb-2" />
            <label className="inline-block">
              <input type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={onPick} data-testid="bulk-bill-file" />
              <span className="text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">Choose PDF files</span>
            </label>
            <p className="text-xs text-muted-foreground mt-2">{files.length ? `${files.length} file(s) selected` : "AI reads each bill; you review matches before anything is created"}</p>
          </div>
        )}

        {/* Step 2: review */}
        {items && !results && (
          <div className="space-y-3" data-testid="bulk-review">
            <div className="text-xs text-muted-foreground">Review the extracted data. Green = delivery number matched an unbilled challan; amber = not found. Uncheck any number to exclude it, edit the bill no / date, or skip a row.</div>
            {items.map((r, i) => (
              <div key={i} className={`rounded-xl border p-3 ${r.skip ? "border-border opacity-60" : "border-border"}`} data-testid={`bulk-review-row-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-mono truncate">{r.filename}</span>
                  <label className="text-xs flex items-center gap-1.5 shrink-0">
                    <input type="checkbox" checked={r.skip} onChange={(e) => setRow(i, { skip: e.target.checked })} data-testid={`bulk-skip-${i}`} />Skip
                  </label>
                </div>
                {r.error ? (
                  <div className="text-xs text-rose-500">{r.error}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="text-[11px] text-muted-foreground">Bill Number</label>
                        <Input className="h-8" value={r.bill_number} onChange={(e) => setRow(i, { bill_number: e.target.value })} data-testid={`bulk-billno-${i}`} />
                        {r.duplicate && <span className="text-[11px] text-rose-500">Already exists — rename or skip</span>}
                      </div>
                      <div>
                        <label className="text-[11px] text-muted-foreground">Bill Date</label>
                        <Input className="h-8" type="date" value={r.bill_date || ""} onChange={(e) => setRow(i, { bill_date: e.target.value })} data-testid={`bulk-billdate-${i}`} />
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-1">Delivery Numbers · {r.delivery_numbers.filter((d) => availSet.has(d)).length} matched / {r.delivery_numbers.filter((d) => !availSet.has(d)).length} not found</div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.delivery_numbers.length === 0 && <span className="text-[11px] text-muted-foreground">None detected</span>}
                      {r.delivery_numbers.map((dn) => {
                        const on = r.selected.includes(dn);
                        const matched = availSet.has(dn);
                        return (
                          <button key={dn} onClick={() => toggleDn(i, dn)} data-testid={`bulk-dn-${i}-${dn}`}
                            className={`text-[11px] font-mono px-2 py-1 rounded-full border transition-colors ${on ? (matched ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-500" : "border-amber-500/50 bg-amber-500/15 text-amber-600 dark:text-amber-400") : "border-border text-muted-foreground line-through"}`}>
                            {dn}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Combobox value={r._add || ""} onChange={(v) => setRow(i, { _add: v })} options={availNumbers.filter((n) => !r.delivery_numbers.includes(n))} placeholder="Add delivery / shipment no" testid={`bulk-add-dn-${i}`} className="flex-1" />
                      <Button size="sm" variant="outline" onClick={() => addDn(i)} data-testid={`bulk-add-dn-btn-${i}`}>Add</Button>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2" data-testid={`bulk-gst-${i}`}>
                      <div><label className="text-[11px] text-muted-foreground">CGST</label><Input className="h-8" type="number" value={r.cgst || ""} onChange={(e) => setRow(i, { cgst: e.target.value })} data-testid={`bulk-cgst-${i}`} /></div>
                      <div><label className="text-[11px] text-muted-foreground">SGST</label><Input className="h-8" type="number" value={r.sgst || ""} onChange={(e) => setRow(i, { sgst: e.target.value })} data-testid={`bulk-sgst-${i}`} /></div>
                      <div><label className="text-[11px] text-muted-foreground">IGST</label><Input className="h-8" type="number" value={r.igst || ""} onChange={(e) => setRow(i, { igst: e.target.value })} data-testid={`bulk-igst-${i}`} /></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 3: results */}
        {results && (
          <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid="bulk-bill-results">
            {results.results.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs truncate min-w-0">{r.bill_number || "(no number)"}</span>
                {r.created
                  ? <span className="text-emerald-500 text-xs whitespace-nowrap">✓ {r.matched} DN · {fmtCurrency(r.value_raised || 0)}</span>
                  : <span className="text-rose-500 text-xs whitespace-nowrap">{r.error}</span>}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {!items && !results && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={scan} disabled={scanning || !files.length} data-testid="bulk-scan-run">{scanning ? "Scanning…" : `Scan ${files.length || ""}`}</Button>
            </>
          )}
          {items && !results && (
            <>
              <Button variant="outline" onClick={() => { setItems(null); setFiles([]); }} data-testid="bulk-back">Back</Button>
              <Button onClick={finalize} disabled={creating || readyCount === 0} data-testid="bulk-create-run">{creating ? "Creating…" : `Create ${readyCount} Bill${readyCount === 1 ? "" : "s"}`}</Button>
            </>
          )}
          {results && <Button onClick={onDone} data-testid="bulk-import-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPayment({ bill, onClose, onSaved }) {
  const [f, setF] = useState({ value_received: String(bill.value_received || ""), tax: String(bill.tax || ""), received_date: bill.received_date || "", gst_retained: String(bill.gst_retained || ""), gst_released: !!bill.gst_released, gst_release_note: bill.gst_release_note || "", gst_release_date: bill.gst_release_date || "" });
  const [ocr, setOcr] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bills-register/payment-ocr`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") } });
      setF((p) => ({ ...p, value_received: data.value_received ? String(data.value_received) : p.value_received, tax: data.tds ? String(data.tds) : p.tax, received_date: data.received_date || p.received_date, gst_retained: data.gst_retained ? String(data.gst_retained) : p.gst_retained }));
      toast.success("Payment details read from document");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the document");
    } finally {
      setOcr(false);
      e.target.value = "";
    }
  };
  const save = async () => {
    try {
      await api.put(`/bills-register/${bill.id}`, {
        bill_number: bill.bill_number, bill_type: bill.bill_type, bill_date: bill.bill_date || "",
        value_raised: bill.value_raised || 0, value_received: Number(f.value_received) || 0,
        received_date: f.received_date, tax: Number(f.tax) || 0, remarks: bill.remarks || "",
        gst_enabled: !!bill.gst_enabled, gst_pct: bill.gst_pct || 0, gst_amount: bill.gst_amount || 0,
        cgst: bill.cgst || 0, sgst: bill.sgst || 0, igst: bill.igst || 0,
        gst_retained: Number(f.gst_retained) || 0, gst_released: f.gst_released, gst_release_note: f.gst_release_note, gst_release_date: f.gst_release_date,
        delivery_numbers: bill.delivery_numbers || [], hub_id: bill.hub_id || "",
      });
      toast.success("Payment recorded"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Record Payment · {bill.bill_number}</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 flex items-center gap-3">
          <FileUp className="h-5 w-5 text-emerald-500 shrink-0" />
          <div className="min-w-0"><div className="text-sm font-medium">Auto-fill from receipt</div><div className="text-xs text-muted-foreground">Upload the payment advice PDF — AI reads amount, TDS &amp; date</div></div>
          <label className="ml-auto shrink-0"><input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFile} disabled={ocr} data-testid="payment-ocr-file" /><span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">{ocr ? "Reading…" : "Upload PDF"}</span></label>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Value Received"><Input type="number" value={f.value_received} onChange={(e) => setF({ ...f, value_received: e.target.value })} data-testid="payment-value-input" /></Field>
          <Field label="TDS"><Input type="number" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} data-testid="payment-tds-input" /></Field>
          <Field label="Received Date"><Input type="date" value={f.received_date} onChange={(e) => setF({ ...f, received_date: e.target.value })} data-testid="payment-date-input" /></Field>
          <Field label="GST Retention"><Input type="number" value={f.gst_retained} onChange={(e) => setF({ ...f, gst_retained: e.target.value })} data-testid="payment-gst-retained-input" /></Field>
        </div>
        {Number(f.gst_retained) > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="payment-gst-retention-box">
            <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.gst_released} onCheckedChange={(v) => setF({ ...f, gst_released: v, gst_release_date: v && !f.gst_release_date ? today() : f.gst_release_date })} data-testid="payment-gst-release-toggle" />Mark GST retention released</label>
            {f.gst_released ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Release Date"><Input type="date" value={f.gst_release_date} onChange={(e) => setF({ ...f, gst_release_date: e.target.value })} data-testid="payment-gst-release-date" /></Field>
                <Field label="Release Note"><Input value={f.gst_release_note} onChange={(e) => setF({ ...f, gst_release_note: e.target.value })} placeholder="e.g. released after GSTR filing" data-testid="payment-gst-release-note" /></Field>
              </div>
            ) : <p className="text-xs text-amber-500">This GST amount is held as retention — toggle above once it is released.</p>}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-payment-button">Save Payment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentsBulk({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState(null);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") });
  const scan = async () => {
    if (!file) return toast.error("Choose a payment file");
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/bills-register/payment-scan`, fd, { headers: authHeaders() });
      setRows((data.payments || []).map((p) => ({ ...p })));
      if (!data.payments?.length) toast.error("No payments detected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read the file"); }
    finally { setScanning(false); }
  };
  const setR = (i, patch) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const apply = async () => {
    const payments = rows.filter((r) => r.bill_number).map((r) => ({ bill_number: r.bill_number, value_received: Number(r.value_received) || 0, tds: Number(r.tds) || 0, gst_retained: Number(r.gst_retained) || 0, received_date: r.received_date }));
    if (!payments.length) return toast.error("Nothing to apply");
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/bills-register/payment-apply`, { payments }, { headers: { ...authHeaders(), "Content-Type": "application/json" } });
      setResults(data); toast.success(`Updated ${data.updated} bill(s)${data.failed ? `, ${data.failed} skipped` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payments-bulk-dialog">
        <DialogHeader><DialogTitle>Record Payments — one file, all bills</DialogTitle></DialogHeader>
        {!rows && !results && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-5 text-center">
            <FileUp className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
            <label className="inline-block"><input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="payments-bulk-file" /><span className="text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">Choose payment file</span></label>
            <p className="text-xs text-muted-foreground mt-2">{file ? file.name : "AI reads every bill's received amount, TDS & date from one advice"}</p>
          </div>
        )}
        {rows && !results && (
          <div className="space-y-2" data-testid="payments-review">
            {rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No payment lines found</p>}
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 items-end rounded-lg border border-border p-2" data-testid={`pay-row-${i}`}>
                <div><label className="text-[11px] text-muted-foreground">Bill No {r.matched ? "✓" : "⚠"}</label><Input className="h-8" value={r.bill_number} onChange={(e) => setR(i, { bill_number: e.target.value })} data-testid={`pay-billno-${i}`} /></div>
                <div><label className="text-[11px] text-muted-foreground">Received</label><Input className="h-8" type="number" value={r.value_received} onChange={(e) => setR(i, { value_received: e.target.value })} data-testid={`pay-value-${i}`} /></div>
                <div><label className="text-[11px] text-muted-foreground">TDS</label><Input className="h-8" type="number" value={r.tds} onChange={(e) => setR(i, { tds: e.target.value })} data-testid={`pay-tds-${i}`} /></div>
                <div><label className="text-[11px] text-muted-foreground">GST Retention</label><Input className="h-8" type="number" value={r.gst_retained || ""} onChange={(e) => setR(i, { gst_retained: e.target.value })} data-testid={`pay-retained-${i}`} /></div>
                <div><label className="text-[11px] text-muted-foreground">Date</label><Input className="h-8" type="date" value={r.received_date || ""} onChange={(e) => setR(i, { received_date: e.target.value })} data-testid={`pay-date-${i}`} /></div>
                {!r.matched && <div className="col-span-5 text-[11px] text-amber-500">No bill with this number — fix it or it will be skipped</div>}
              </div>
            ))}
          </div>
        )}
        {results && (
          <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid="payments-results">
            {results.results.map((r, i) => (<div key={i} className="flex items-center justify-between px-3 py-2 text-sm"><span className="font-mono text-xs">{r.bill_number || "(none)"}</span>{r.updated ? <span className="text-emerald-500 text-xs">✓ updated</span> : <span className="text-rose-500 text-xs">{r.error}</span>}</div>))}
          </div>
        )}
        <DialogFooter>
          {!rows && !results && (<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={scan} disabled={scanning || !file} data-testid="payments-scan-run">{scanning ? "Scanning…" : "Scan"}</Button></>)}
          {rows && !results && (<><Button variant="outline" onClick={() => { setRows(null); setFile(null); }}>Back</Button><Button onClick={apply} disabled={applying} data-testid="payments-apply-run">{applying ? "Applying…" : `Apply ${rows.filter((r) => r.bill_number).length}`}</Button></>)}
          {results && <Button onClick={onDone} data-testid="payments-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Expenditure() {
  const { selectedHub, hubs } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ocr, setOcr] = useState(false);
  const empty = { expense_date: today(), category: "Diesel", vehicle_number: "", narration: "", amount: "", gst_enabled: false, gst_amount: "", diesel_volume: "", diesel_rate: "", location: "", hub_id: "" };
  const [f, setF] = useState(empty);
  const [vehicles, setVehicles] = useState([]);
  const load = useCallback(async () => setRows((await api.get("/expenses", { params: { hub: selectedHub } })).data), [selectedHub]);
  useEffect(() => { load(); api.get("/vehicles", { params: { hub: selectedHub } }).then((r) => setVehicles(r.data.map((v) => v.vehicle_number))); }, [load, selectedHub]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/expenses/ocr`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") } });
      setF((p) => ({ ...p, category: data.category || p.category, amount: data.amount ? String(data.amount) : p.amount, gst_enabled: data.gst_enabled || p.gst_enabled, gst_amount: data.gst_amount ? String(data.gst_amount) : p.gst_amount, narration: data.narration || p.narration, expense_date: data.expense_date || p.expense_date, diesel_volume: data.diesel_volume ? String(data.diesel_volume) : p.diesel_volume, diesel_rate: data.diesel_rate ? String(data.diesel_rate) : p.diesel_rate, location: data.location || p.location }));
      toast.success("Expense details read from document");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the document"); }
    finally { setOcr(false); e.target.value = ""; }
  };
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r, amount: String(r.amount ?? ""), gst_amount: String(r.gst_amount ?? ""), diesel_volume: String(r.diesel_volume ?? ""), diesel_rate: String(r.diesel_rate ?? "") }); setOpen(true); };
  const del = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); };
  const save = async () => {
    if (!f.amount) return toast.error("Amount required");
    const payload = { ...f, amount: Number(f.amount) || 0, gst_amount: Number(f.gst_amount) || 0, diesel_volume: Number(f.diesel_volume) || 0, diesel_rate: Number(f.diesel_rate) || 0, hub_id: f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id) };
    if (editing) { await api.put(`/expenses/${editing}`, payload); toast.success("Expense updated"); }
    else { await api.post("/expenses", payload); toast.success("Expense recorded"); }
    setOpen(false); setF(empty); setEditing(null); load();
  };
  const filtered = rows.filter((r) => !q || (r.narration || "").toLowerCase().includes(q.toLowerCase()) || (r.vehicle_number || "").toLowerCase().includes(q.toLowerCase()) || (r.category || "").toLowerCase().includes(q.toLowerCase()));
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows } = usePaged(filtered);
  const total = filtered.reduce((s, r) => s + (r.amount || 0), 0);
  const isDiesel = f.category === "Diesel";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input className="w-64" placeholder="Search category / vehicle / note" value={q} onChange={(e) => setQ(e.target.value)} data-testid="expense-search" />
        <div className="ml-auto flex items-center gap-2">
          <PageSize value={pageSize} onChange={setPageSize} />
          <Button onClick={() => { setEditing(null); setF({ ...empty }); setOpen(true); }} data-testid="add-expense-button"><Plus className="h-4 w-4 mr-1.5" />Add Expense</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-left">Category</th><th className="py-3 px-4 text-left">Vehicle</th>
              <th className="py-3 px-4 text-left">Narration</th><th className="py-3 px-4 text-left">Diesel (L · ₹/L · Loc)</th>
              <th className="py-3 px-4 text-right">GST</th><th className="py-3 px-4 text-right">Amount</th><th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`expense-row-${r.id}`}>
                <td className="py-3 px-4">{r.expense_date ? dt(r.expense_date) : "-"}</td>
                <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">{r.category}</span></td>
                <td className="py-3 px-4 font-mono text-xs">{r.vehicle_number || "-"}</td>
                <td className="py-3 px-4">{r.narration || "-"}</td>
                <td className="py-3 px-4 text-xs">{r.diesel_volume ? `${fmtNumber(r.diesel_volume)} L · ₹${fmtNumber(r.diesel_rate)} · ${r.location || "-"}` : "-"}</td>
                <td className="py-3 px-4 text-right font-mono">{r.gst_enabled ? fmtCurrency(r.gst_amount) : "-"}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtCurrency(r.amount)}</td>
                <td className="py-3 px-4 text-center"><div className="flex items-center justify-center gap-3"><button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-expense-${r.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-expense-${r.id}`}><Trash2 className="h-4 w-4" /></button></div></td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={8} className="py-16 text-center text-muted-foreground">No expenses yet</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4" colSpan={6}>Total</td><td className="py-3 px-4 text-right font-mono" colSpan={2}>{fmtCurrency(total)}</td></tr></tfoot>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 flex items-center gap-3">
            <FileUp className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0"><div className="text-sm font-medium">Auto-fill from bill</div><div className="text-xs text-muted-foreground">Upload the expense bill — AI reads amount, GST & diesel details</div></div>
            <label className="ml-auto shrink-0"><input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFile} disabled={ocr} data-testid="expense-ocr-file" /><span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">{ocr ? "Reading…" : "Upload"}</span></label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><Input type="date" value={f.expense_date} onChange={(e) => setF({ ...f, expense_date: e.target.value })} data-testid="expense-date-input" /></Field>
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Diesel", "Repair", "Tyre", "Toll", "Insurance", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Vehicle (own)">
              <Select value={f.vehicle_number || "none"} onValueChange={(v) => setF({ ...f, vehicle_number: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="expense-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— none —</SelectItem>{vehicles.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount *"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" /></Field>
            <Field label="Narration" className="col-span-2"><Input value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })} data-testid="expense-narration-input" /></Field>
          </div>
          <div className="flex items-center gap-3 py-1">
            <Switch checked={f.gst_enabled} onCheckedChange={(v) => setF({ ...f, gst_enabled: v })} data-testid="expense-gst-toggle" />
            <span className="text-sm font-medium">Bill has GST</span>
            {f.gst_enabled && <Input type="number" className="w-40 ml-2" placeholder="GST amount" value={f.gst_amount} onChange={(e) => setF({ ...f, gst_amount: e.target.value })} data-testid="expense-gst-amount-input" />}
          </div>
          {isDiesel && (
            <div className="grid grid-cols-3 gap-4" data-testid="expense-diesel-fields">
              <Field label="Diesel Volume (L)"><Input type="number" value={f.diesel_volume} onChange={(e) => setF({ ...f, diesel_volume: e.target.value })} data-testid="expense-diesel-volume-input" /></Field>
              <Field label="Rate (₹/L)"><Input type="number" value={f.diesel_rate} onChange={(e) => setF({ ...f, diesel_rate: e.target.value })} data-testid="expense-diesel-rate-input" /></Field>
              <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="expense-location-input" /></Field>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-expense-button">{editing ? "Update" : "Save"} Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthLabel = (m) => {
  const [y, mm] = (m || "").split("-").map(Number);
  return y && mm >= 1 && mm <= 12 ? `${MONTH_NAMES[mm - 1]} ${y}` : (m || "Unknown");
};

function Fastag() {
  const { selectedHub, hubs } = useApp();
  const [groups, setGroups] = useState([]);
  const [ocr, setOcr] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMonths, setOpenMonths] = useState({});
  const [openVeh, setOpenVeh] = useState("");
  const [q, setQ] = useState("");
  const emptyForm = { vehicle_number: "", period_from: "", period_to: "", total_deduction: "", transactions: [] };
  const [f, setF] = useState(emptyForm);
  const load = useCallback(async () => setGroups((await api.get("/fastag/monthly", { params: { hub: selectedHub } })).data), [selectedHub]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (groups.length) setOpenMonths((p) => (Object.keys(p).length ? p : { [groups[0].month]: true })); }, [groups]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/fastag/statement-ocr`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") } });
      setF({
        vehicle_number: data.vehicle_number || "",
        period_from: data.period_from || "",
        period_to: data.period_to || "",
        total_deduction: data.total_deduction ? String(data.total_deduction) : "",
        transactions: data.transactions || [],
      });
      setOpen(true);
      toast.success(`Read ${data.transactions?.length || 0} toll transactions`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the statement"); }
    finally { setOcr(false); e.target.value = ""; }
  };

  const setTxn = (i, key, val) => setF((p) => ({ ...p, transactions: p.transactions.map((t, j) => (j === i ? { ...t, [key]: val } : t)) }));
  const removeTxn = (i) => setF((p) => ({ ...p, transactions: p.transactions.filter((_, j) => j !== i) }));
  const computedTotal = f.transactions.reduce((s, t) => s + (Number(t.amount) || 0), 0);

  const save = async () => {
    const payload = {
      vehicle_number: (f.vehicle_number || "").toUpperCase(),
      period_from: f.period_from, period_to: f.period_to,
      total_deduction: Number(f.total_deduction) || computedTotal,
      hub_id: selectedHub !== "all" ? selectedHub : (hubs[0]?.id || ""),
      transactions: f.transactions.map((t) => ({ txn_date: t.txn_date || "", txn_time: t.txn_time || "", plaza: t.plaza || "", amount: Number(t.amount) || 0 })),
    };
    try {
      const { data } = await api.post("/fastag/statements", payload);
      if (data.inserted === 0) toast.warning(`Nothing new — all ${data.skipped} transactions were already captured`);
      else toast.success(`Saved ${data.inserted} toll transactions${data.skipped ? ` · ${data.skipped} duplicates skipped` : ""}`);
      setOpen(false); setF(emptyForm); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save statement");
    }
  };
  const delGroup = async (month, vehicle) => {
    if (!window.confirm(`Delete all ${monthLabel(month)} toll entries for ${vehicle}?`)) return;
    await api.delete("/fastag/txns", { params: { vehicle_number: vehicle, month } });
    toast.success("Deleted"); load();
  };
  const delTxn = async (id) => { await api.delete(`/fastag/txns/${id}`); toast.success("Deleted"); load(); };

  const ql = q.toLowerCase();
  const filtered = groups.map((g) => {
    if (!q || g.month.includes(q)) return g;
    const vs = g.vehicles.filter((v) => v.vehicle_number.toLowerCase().includes(ql));
    return { ...g, vehicles: vs, total: vs.reduce((s, v) => s + v.total, 0), txn_count: vs.reduce((s, v) => s + v.txn_count, 0) };
  }).filter((g) => g.vehicles.length > 0);
  const nowMonth = new Date().toISOString().slice(0, 7);
  const cur = groups.find((g) => g.month === nowMonth);
  const grandTotal = filtered.reduce((s, g) => s + g.total, 0);
  const txnTotal = filtered.reduce((s, g) => s + g.txn_count, 0);
  const vehSet = new Set(); filtered.forEach((g) => g.vehicles.forEach((v) => vehSet.add(v.vehicle_number)));
  const cards = [
    { label: "This Month", value: fmtCurrency(cur?.total || 0), sub: monthLabel(nowMonth), icon: Banknote, testid: "fastag-card-month" },
    { label: "Vehicles This Month", value: cur?.vehicles.length || 0, sub: "with toll activity", icon: Truck, testid: "fastag-card-vehicles" },
    { label: "Total Deduction", value: fmtCurrency(grandTotal), sub: q ? "filtered view" : "all time", icon: Gauge, testid: "fastag-card-total" },
    { label: "Toll Transactions", value: fmtNumber(txnTotal), sub: `${vehSet.size} vehicles`, icon: FileSpreadsheet, testid: "fastag-card-txns" },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border bg-card p-4" data-testid={c.testid}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{c.label}</span>
              <c.icon className="h-4 w-4 text-primary" />
            </div>
            <div className="mt-2 text-xl font-bold font-mono">{c.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input className="w-64" placeholder="Search vehicle / month (YYYY-MM)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fastag-search" />
        <div className="ml-auto flex items-center gap-2">
          <label>
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFile} disabled={ocr} data-testid="fastag-upload-input" />
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="fastag-upload-button"><FileUp className="h-4 w-4" />{ocr ? "Reading…" : "Upload Statement"}</span>
          </label>
        </div>
      </div>
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 mb-4 text-xs text-muted-foreground">
        Upload your FASTag statement (PDF / image) from the ICICI FASTag portal. AI reads every toll transaction (date, plaza, amount) — each is filed under its actual date's month, so overlapping statements never double-count.
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm" data-testid="fastag-statements-table">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="py-3 px-4 text-left w-8"></th>
              <th className="py-3 px-4 text-left">Vehicle</th>
              <th className="py-3 px-4 text-right">Toll Txns</th>
              <th className="py-3 px-4 text-right">Total Deduction</th>
              <th className="py-3 px-4 text-center w-16">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <React.Fragment key={g.month}>
                <tr className="border-t border-border bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={() => setOpenMonths((p) => ({ ...p, [g.month]: !p[g.month] }))} data-testid={`fastag-month-${g.month}`}>
                  <td className="py-2.5 px-4"><ChevronRight className={`h-4 w-4 transition-transform ${openMonths[g.month] ? "rotate-90" : ""}`} /></td>
                  <td className="py-2.5 px-4 font-semibold">{monthLabel(g.month)}<span className="ml-2 text-xs font-normal text-muted-foreground">{g.vehicles.length} vehicle{g.vehicles.length === 1 ? "" : "s"}</span></td>
                  <td className="py-2.5 px-4 text-right font-mono text-muted-foreground">{g.txn_count}</td>
                  <td className="py-2.5 px-4 text-right font-mono font-bold text-primary">{fmtCurrency(g.total)}</td>
                  <td></td>
                </tr>
                {openMonths[g.month] && g.vehicles.map((v) => {
                  const vk = `${g.month}|${v.vehicle_number}`;
                  const vOpen = openVeh === vk;
                  return (
                    <React.Fragment key={vk}>
                      <tr className="border-t border-border/60 hover:bg-accent/30" data-testid={`fastag-row-${g.month}-${v.vehicle_number}`}>
                        <td className="py-2.5 px-4"><button onClick={() => setOpenVeh(vOpen ? "" : vk)} data-testid={`fastag-expand-${g.month}-${v.vehicle_number}`}><ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${vOpen ? "rotate-90" : ""}`} /></button></td>
                        <td className="py-2.5 px-4 font-mono text-xs pl-6">{v.vehicle_number}</td>
                        <td className="py-2.5 px-4 text-right font-mono">{v.txn_count}</td>
                        <td className="py-2.5 px-4 text-right font-mono font-semibold">{fmtCurrency(v.total)}</td>
                        <td className="py-2.5 px-4 text-center"><button onClick={() => delGroup(g.month, v.vehicle_number)} className="text-rose-500 hover:opacity-70" data-testid={`fastag-delete-${g.month}-${v.vehicle_number}`}><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                      {vOpen && (
                        <tr className="bg-muted/20" data-testid={`fastag-detail-${g.month}-${v.vehicle_number}`}>
                          <td colSpan={5} className="px-6 py-3">
                            <div className="rounded-lg border border-border overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-muted/40 text-muted-foreground"><tr><th className="py-2 px-3 text-left">Date</th><th className="py-2 px-3 text-left">Time</th><th className="py-2 px-3 text-left">Toll Plaza</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3 w-8"></th></tr></thead>
                                <tbody>
                                  {v.transactions.map((t) => (
                                    <tr key={t.id} className="border-t border-border"><td className="py-2 px-3">{t.txn_date ? dt(t.txn_date) : "-"}</td><td className="py-2 px-3">{t.txn_time || "-"}</td><td className="py-2 px-3">{t.plaza || "-"}</td><td className="py-2 px-3 text-right font-mono">{fmtCurrency(t.amount)}</td><td className="py-2 px-3 text-center"><button onClick={() => delTxn(t.id)} className="text-rose-500 hover:opacity-70" data-testid={`fastag-txn-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button></td></tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="py-16 text-center text-muted-foreground">No FASTag data yet — upload a statement to auto-capture toll deductions</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-border bg-muted/40 font-semibold"><td className="py-3 px-4" colSpan={3}>Total Deduction</td><td className="py-3 px-4 text-right font-mono">{fmtCurrency(grandTotal)}</td><td></td></tr></tfoot>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fastag-review-dialog">
          <DialogHeader><DialogTitle>Review FASTag Statement</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Vehicle Number"><Input value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value.toUpperCase() })} data-testid="fastag-vehicle-input" /></Field>
            <Field label="Period From"><Input type="date" value={f.period_from} onChange={(e) => setF({ ...f, period_from: e.target.value })} data-testid="fastag-from-input" /></Field>
            <Field label="Period To"><Input type="date" value={f.period_to} onChange={(e) => setF({ ...f, period_to: e.target.value })} data-testid="fastag-to-input" /></Field>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            Each transaction is filed under the month of its own date — overlapping statements are de-duplicated automatically.
          </div>
          <div className="rounded-lg border border-border overflow-hidden mt-1">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="py-2 px-3 text-left">Date</th><th className="py-2 px-3 text-left">Time</th><th className="py-2 px-3 text-left">Toll Plaza</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3 w-8"></th></tr></thead>
              <tbody>
                {f.transactions.map((t, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`fastag-review-line-${i}`}>
                    <td className="py-1.5 px-2"><Input type="date" className="h-8" value={t.txn_date} onChange={(e) => setTxn(i, "txn_date", e.target.value)} /></td>
                    <td className="py-1.5 px-2"><Input type="time" className="h-8" value={t.txn_time || ""} onChange={(e) => setTxn(i, "txn_time", e.target.value)} /></td>
                    <td className="py-1.5 px-2"><Input className="h-8" value={t.plaza} onChange={(e) => setTxn(i, "plaza", e.target.value)} /></td>
                    <td className="py-1.5 px-2"><Input type="number" className="h-8 text-right" value={t.amount} onChange={(e) => setTxn(i, "amount", e.target.value)} /></td>
                    <td className="py-1.5 px-2 text-center"><button onClick={() => removeTxn(i)} className="text-rose-500" data-testid={`fastag-remove-line-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
                {f.transactions.length === 0 && <tr><td colSpan={5} className="py-3 px-3 text-center text-muted-foreground">No toll lines detected</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-sm mt-1 px-1">
            <span className="text-muted-foreground">{f.transactions.length} toll transactions</span>
            <span className="text-muted-foreground">Total Deduction: <span className="font-mono font-semibold text-foreground" data-testid="fastag-review-total">{fmtCurrency(Number(f.total_deduction) || computedTotal)}</span></span>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => save()} data-testid="fastag-save-button">Save Statement</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
